import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { useLocation } from 'react-router';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useNavigate } from '@/hooks/use-navigate';
import { useAuth } from '@/providers/auth';
import { useCheckAndExecutePendingAction } from '@/hooks/check-and-execute-pending-action';
import { ACTION_PARAMS, getSearchWithoutActionParams } from '@/hooks/use-filters-panel-state';
import { resolvePendingWishlistProduct } from '@/lib/pending-wishlist-product';
import {
    WishlistOverlayContext,
    type LoginDrawerRequest,
    type WishlistBulkSaveRequest,
    type WishlistSelectionRequest,
} from '@/providers/wishlist-overlay-context';

const LoginDrawer = lazy(() => import('@/components/login/login-drawer'));
const WishlistSelectionDrawer = lazy(() =>
    import('@/components/wishlist/wishlist-selection-drawer').then((module) => ({
        default: module.WishlistSelectionDrawer,
    }))
);
const WishlistBulkSaveDrawer = lazy(() =>
    import('@/components/wishlist/wishlist-bulk-save-drawer').then((module) => ({
        default: module.WishlistBulkSaveDrawer,
    }))
);

function getCleanCurrentPath(): string {
    if (typeof window === 'undefined') {
        return '/';
    }
    const url = new URL(window.location.href);
    for (const key of ACTION_PARAMS) {
        url.searchParams.delete(key);
    }
    return url.pathname + url.search;
}

function parseProductIds(raw: unknown): string[] {
    if (typeof raw !== 'string' || !raw.trim()) {
        return [];
    }
    try {
        const parsed = JSON.parse(raw) as unknown;
        return Array.isArray(parsed)
            ? parsed.filter((id): id is string => typeof id === 'string' && id.length > 0)
            : [];
    } catch {
        return [];
    }
}

/**
 * Hosts a single LoginDrawer and WishlistSelectionDrawer for PLP tiles and other product grids.
 * Pending addToWishlist actions after login are handled here (not per tile).
 */
export default function WishlistOverlayProvider({ children }: PropsWithChildren) {
    const config = useConfig();
    const location = useLocation();
    const navigate = useNavigate();
    const session = useAuth();
    const isAuthenticated = Boolean(session?.userType === 'registered' && session?.customerId);

    const [loginLoaded, setLoginLoaded] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);
    const [loginRequest, setLoginRequest] = useState<LoginDrawerRequest>({});

    const [wishlistLoaded, setWishlistLoaded] = useState(false);
    const [wishlistOpen, setWishlistOpen] = useState(false);
    const [wishlistRequest, setWishlistRequest] = useState<WishlistSelectionRequest | null>(null);

    const [bulkWishlistLoaded, setBulkWishlistLoaded] = useState(false);
    const [bulkWishlistOpen, setBulkWishlistOpen] = useState(false);
    const [bulkWishlistRequest, setBulkWishlistRequest] = useState<WishlistBulkSaveRequest | null>(null);

    const closeLoginDrawer = useCallback(() => {
        setLoginOpen(false);
    }, []);

    const openLoginDrawer = useCallback((request: LoginDrawerRequest) => {
        setLoginLoaded(true);
        setLoginRequest(request);
        setLoginOpen(true);
    }, []);

    // Login from the drawer uses a React Router redirect (not a full reload), so loginOpen
    // must be cleared when the session becomes registered.
    useEffect(() => {
        if (isAuthenticated && loginOpen) {
            closeLoginDrawer();
        }
    }, [isAuthenticated, loginOpen, closeLoginDrawer]);

    const openWishlistSelectionDrawer = useCallback((request: WishlistSelectionRequest) => {
        setWishlistLoaded(true);
        setWishlistRequest(request);
        setWishlistOpen(true);
    }, []);

    const openWishlistBulkSaveDrawer = useCallback((request: WishlistBulkSaveRequest) => {
        setBulkWishlistLoaded(true);
        setBulkWishlistRequest(request);
        setBulkWishlistOpen(true);
    }, []);

    const handleWishlistOpenChange = useCallback((open: boolean) => {
        setWishlistOpen(open);
        if (!open) {
            setWishlistRequest(null);
        }
    }, []);

    const handleBulkWishlistOpenChange = useCallback((open: boolean) => {
        setBulkWishlistOpen(open);
        if (!open) {
            setBulkWishlistRequest(null);
        }
    }, []);

    const handlePendingAddToWishlist = useCallback(
        (params: Record<string, unknown>) => {
            closeLoginDrawer();
            const { product, variant } = resolvePendingWishlistProduct(params);
            openWishlistSelectionDrawer({ product, variant });
            void navigate(
                { pathname: location.pathname, search: getSearchWithoutActionParams(location.search) },
                { replace: true }
            );
        },
        [closeLoginDrawer, openWishlistSelectionDrawer, navigate, location.pathname, location.search]
    );

    const handlePendingBulkSaveToWishlist = useCallback(
        (params: Record<string, unknown>) => {
            closeLoginDrawer();
            const productIds = parseProductIds(params.productIds);
            if (productIds.length > 0) {
                openWishlistBulkSaveDrawer({ productIds });
            }
            void navigate(
                { pathname: location.pathname, search: getSearchWithoutActionParams(location.search) },
                { replace: true }
            );
        },
        [closeLoginDrawer, openWishlistBulkSaveDrawer, navigate, location.pathname, location.search]
    );

    useCheckAndExecutePendingAction({
        actionName: 'addToWishlist',
        shouldExecute: (params) => isAuthenticated && Boolean(params.productId),
        onMatch: handlePendingAddToWishlist,
    });

    useCheckAndExecutePendingAction({
        actionName: 'bulkSaveToWishlist',
        shouldExecute: (params) => isAuthenticated && parseProductIds(params.productIds).length > 0,
        onMatch: handlePendingBulkSaveToWishlist,
    });

    const value = useMemo(
        () => ({ openLoginDrawer, openWishlistSelectionDrawer, openWishlistBulkSaveDrawer }),
        [openLoginDrawer, openWishlistSelectionDrawer, openWishlistBulkSaveDrawer]
    );

    return (
        <WishlistOverlayContext.Provider value={value}>
            {children}
            {loginLoaded ? (
                <Suspense fallback={null}>
                    <LoginDrawer
                        isOpen={loginOpen}
                        onOpenChange={setLoginOpen}
                        onSuccess={closeLoginDrawer}
                        isSocialLoginEnabled={Boolean(config.features.socialLogin?.enabled)}
                        returnUrl={loginRequest.returnUrl ?? getCleanCurrentPath()}
                        action={loginRequest.action}
                        actionParams={loginRequest.actionParams}
                    />
                </Suspense>
            ) : null}
            {wishlistLoaded && wishlistRequest ? (
                <Suspense fallback={null}>
                    <WishlistSelectionDrawer
                        open={wishlistOpen}
                        onOpenChange={handleWishlistOpenChange}
                        product={wishlistRequest.product}
                        variant={wishlistRequest.variant}
                    />
                </Suspense>
            ) : null}
            {bulkWishlistLoaded && bulkWishlistRequest ? (
                <Suspense fallback={null}>
                    <WishlistBulkSaveDrawer
                        open={bulkWishlistOpen}
                        onOpenChange={handleBulkWishlistOpenChange}
                        productIds={bulkWishlistRequest.productIds}
                    />
                </Suspense>
            ) : null}
        </WishlistOverlayContext.Provider>
    );
}
